import { ChangeDetectionStrategy, Component, ElementRef, afterRenderEffect, computed, effect, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Achievement } from './achievement.model';
import { AchievementSoundService } from './achievement-sound.service';
import { AchievementsService } from './achievements.service';

/** Largeur de la pastille repliée (la médaille, bordure comprise), en px : cf. `--pill-size`. */
const BADGE_PX = 56;
/** Retraits du texte dans la barre ouverte, bordure comprise : 7 px après la médaille, 21 px à droite. */
const TEXT_PADDING_PX = 28;
const VIEWPORT_MARGIN_PX = 32;

/**
 * Bandeau « Succès obtenu », monté une seule fois dans `app.component`.
 *
 * À la manière des consoles : la médaille apparaît avec un petit carillon, la barre se déroule
 * jusqu'au titre, puis se replie et disparaît. Le survol ou le focus met l'animation en
 * pause ; un clic ouvre le succès dans Mon profil. Les succès obtenus coup sur coup s'enchaînent.
 */
@Component({
  selector: 'app-achievement-toast',
  templateUrl: './achievement-toast.component.html',
  styleUrl: './achievement-toast.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AchievementToastComponent {
  protected readonly achievements = inject(AchievementsService);
  private readonly router = inject(Router);
  private readonly sound = inject(AchievementSoundService);
  private readonly pill = viewChild<ElementRef<HTMLElement>>('pill');
  private readonly text = viewChild<ElementRef<HTMLElement>>('text');

  /** Liste d'au plus un élément : chaque annonce recrée le bandeau et rejoue son animation. */
  protected readonly current = computed(() => {
    const toast = this.achievements.toast();
    return toast ? [toast] : [];
  });

  constructor() {
    effect(() => {
      if (this.achievements.toast()) this.sound.play();
    });

    // La barre s'ouvre jusqu'à la largeur de son texte : mesurée, une largeur `auto` ne s'anime pas.
    afterRenderEffect(() => {
      const pill = this.pill()?.nativeElement;
      const text = this.text()?.nativeElement;
      if (!pill || !text) return;
      const width = Math.min(BADGE_PX + text.offsetWidth + TEXT_PADDING_PX, window.innerWidth - VIEWPORT_MARGIN_PX);
      pill.style.setProperty('--pill-width', `${width}px`);
    });
  }

  /** La fin de l'animation de la barre clôt le bandeau ; celles de l'icône et du texte remontent aussi jusqu'ici. */
  protected onAnimationEnd(event: AnimationEvent): void {
    if (event.target === event.currentTarget) this.achievements.dismissToast();
  }

  protected open(achievement: Achievement): void {
    this.achievements.dismissToast();
    void this.router.navigate(['/profil/succes'], { queryParams: { succes: achievement.id } });
  }
}
